import { AASM_BAND_FILTER_DEFAULTS } from '@/common/utils';
import { type SafeAny } from '@/common/types';

export const DEFAULT_BAND_CUTOFFS = AASM_BAND_FILTER_DEFAULTS;

export interface FilterSection {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

interface Pole {
  realPart: number;
  imaginaryPart: number;
}

const LOWPASS_PROTOTYPE_POLE: Pole = { realPart: -Math.SQRT1_2, imaginaryPart: Math.SQRT1_2 };
const TRANSFORM_CONSTANT = 4;

const warpCutoff = (cutoffHz: number, fs: number): number => {
  const normalized = cutoffHz / (fs / 2);
  return TRANSFORM_CONSTANT * Math.tan((Math.PI * normalized) / 2);
};

const complexSqrt = (realPart: number, imaginaryPart: number): Pole => {
  const magnitude = Math.hypot(realPart, imaginaryPart);
  return {
    realPart: Math.sqrt((magnitude + realPart) / 2),
    imaginaryPart: (imaginaryPart < 0 ? -1 : 1) * Math.sqrt(Math.max(0, (magnitude - realPart) / 2))
  };
};

const toDigitalPole = (pole: Pole): Pole => {
  const denominatorReal = TRANSFORM_CONSTANT - pole.realPart;
  const denominatorImaginary = -pole.imaginaryPart;
  const denominatorMagnitudeSquared =
    denominatorReal * denominatorReal + denominatorImaginary * denominatorImaginary;
  const numeratorReal = TRANSFORM_CONSTANT + pole.realPart;
  const numeratorImaginary = pole.imaginaryPart;
  return {
    realPart:
      (numeratorReal * denominatorReal + numeratorImaginary * denominatorImaginary) /
      denominatorMagnitudeSquared,
    imaginaryPart:
      (numeratorImaginary * denominatorReal - numeratorReal * denominatorImaginary) /
      denominatorMagnitudeSquared
  };
};

const conjugatePairMagnitudeAt = (x: number, pole: Pole): number =>
  x * x -
  2 * x * pole.realPart +
  (pole.realPart * pole.realPart + pole.imaginaryPart * pole.imaginaryPart);

const createFilterSection = (zero: number, pole: Pole, gain: number): FilterSection => ({
  b0: gain,
  b1: -2 * gain * zero,
  b2: gain * zero * zero,
  a1: -2 * pole.realPart,
  a2: pole.realPart * pole.realPart + pole.imaginaryPart * pole.imaginaryPart
});

export const createBandpassFilter = (
  lowCut: number,
  highCut: number,
  fs: number
): FilterSection[] => {
  const nyquist = fs / 2;
  if (!(lowCut > 0) || !(highCut < nyquist) || !(lowCut < highCut)) {
    throw new Error(`Invalid band-pass cutoffs: lowCut=${lowCut}, highCut=${highCut}, fs=${fs}`);
  }

  const warpedLow = warpCutoff(lowCut, fs);
  const warpedHigh = warpCutoff(highCut, fs);
  const centerFrequency = Math.sqrt(warpedLow * warpedHigh);
  const bandwidth = warpedHigh - warpedLow;

  const halfBandwidth = bandwidth / 2;
  const scaledReal = LOWPASS_PROTOTYPE_POLE.realPart * halfBandwidth;
  const scaledImaginary = LOWPASS_PROTOTYPE_POLE.imaginaryPart * halfBandwidth;
  const branch = complexSqrt(
    scaledReal * scaledReal - scaledImaginary * scaledImaginary - centerFrequency * centerFrequency,
    2 * scaledReal * scaledImaginary
  );
  const analogPoleA: Pole = {
    realPart: scaledReal + branch.realPart,
    imaginaryPart: scaledImaginary + branch.imaginaryPart
  };
  const analogPoleB: Pole = {
    realPart: scaledReal - branch.realPart,
    imaginaryPart: scaledImaginary - branch.imaginaryPart
  };

  const digitalPoleA = toDigitalPole(analogPoleA);
  const digitalPoleB = toDigitalPole(analogPoleB);

  const prototypeGain = bandwidth * bandwidth;
  const gainCorrection =
    (prototypeGain * TRANSFORM_CONSTANT * TRANSFORM_CONSTANT) /
    (conjugatePairMagnitudeAt(TRANSFORM_CONSTANT, analogPoleA) *
      conjugatePairMagnitudeAt(TRANSFORM_CONSTANT, analogPoleB));

  const dcBlockingSection = createFilterSection(1, digitalPoleA, 1);
  const nyquistBlockingSection = createFilterSection(-1, digitalPoleB, gainCorrection);

  return [nyquistBlockingSection, dcBlockingSection];
};

const filterCache = new Map<string, FilterSection[]>();

export const getBandpassFilter = (lowCut: number, highCut: number, fs: number): FilterSection[] => {
  const key = `${lowCut}|${highCut}|${fs}`;
  let sections = filterCache.get(key);
  if (!sections) {
    sections = createBandpassFilter(lowCut, highCut, fs);
    filterCache.set(key, sections);
  }
  return sections;
};

const dcGain = (section: FilterSection): number =>
  (section.b0 + section.b1 + section.b2) / (1 + section.a1 + section.a2);

const stepState = (section: FilterSection): [number, number] => {
  const steadyState = dcGain(section);
  return [steadyState - section.b0, steadyState * (1 + section.a1) - section.b0 - section.b1];
};

const initialFilterState = (sections: FilterSection[]): [number, number][] => {
  let gainSoFar = 1;
  return sections.map((section) => {
    const [zi1, zi2] = stepState(section);
    const state: [number, number] = [zi1 * gainSoFar, zi2 * gainSoFar];
    gainSoFar *= dcGain(section);
    return state;
  });
};

const filterForward = (
  samples: Float64Array,
  sections: FilterSection[],
  state: [number, number][]
): Float64Array => {
  let data = samples;
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    const output = new Float64Array(data.length);
    let [delay1, delay2] = state[sectionIndex];
    for (let i = 0; i < data.length; i++) {
      const input = data[i];
      const y = section.b0 * input + delay1;
      delay1 = section.b1 * input - section.a1 * y + delay2;
      delay2 = section.b2 * input - section.a2 * y;
      output[i] = y;
    }
    data = output;
  }
  return data;
};

const reflectPad = (
  samples: Float64Array,
  padLength: number
): { padded: Float64Array; appliedPad: number } => {
  const length = samples.length;
  const appliedPad = Math.max(0, Math.min(padLength, length - 1));
  if (appliedPad === 0) return { padded: samples.slice(), appliedPad: 0 };

  const padded = new Float64Array(length + appliedPad * 2);
  for (let i = 0; i < appliedPad; i++) {
    padded[i] = 2 * samples[0] - samples[appliedPad - i];
  }
  padded.set(samples, appliedPad);
  for (let i = 0; i < appliedPad; i++) {
    padded[appliedPad + length + i] = 2 * samples[length - 1] - samples[length - 2 - i];
  }

  return { padded, appliedPad };
};

export const applyZeroPhaseFilter = (samples: number[], sections: FilterSection[]): number[] => {
  if (samples.length === 0) return samples;

  const padLength = 3 * (2 * sections.length + 1);
  const { padded, appliedPad } = reflectPad(Float64Array.from(samples), padLength);
  if (padded.length === 0) return samples.slice();

  const state = initialFilterState(sections);
  const forward = filterForward(
    padded,
    sections,
    state.map(([zi1, zi2]) => [zi1 * padded[0], zi2 * padded[0]])
  );

  forward.reverse();
  const backward = filterForward(
    forward,
    sections,
    state.map(([zi1, zi2]) => [zi1 * forward[0], zi2 * forward[0]])
  );

  const resultLength = backward.length - appliedPad * 2;
  const result = new Float64Array(resultLength);
  for (let i = 0; i < resultLength; i++) {
    result[i] = backward[backward.length - 1 - appliedPad - i];
  }
  return Array.from(result);
};

export const filterSignalBand = (
  values: number[],
  lowCut: number,
  highCut: number,
  fs: number
): number[] => applyZeroPhaseFilter(values, getBandpassFilter(lowCut, highCut, fs));

export interface BandFilterConfig {
  cutoffFor: (signalName: string) => { lowCut: number; highCut: number } | undefined;
  sampleRateFor: (signalName: string, values: number[]) => number | undefined;
}

const memoByOwner = new WeakMap<object, Map<string, number[]>>();

const getMemoFor = (owner: object): Map<string, number[]> => {
  let memo = memoByOwner.get(owner);
  if (!memo) {
    memo = new Map();
    memoByOwner.set(owner, memo);
  }
  return memo;
};

const isFlatNumericArray = (values: unknown): values is number[] =>
  Array.isArray(values) && values.length > 0 && !Array.isArray(values[0]);

const filterOneSignal = <T extends number[] | number[][]>(
  label: string,
  values: T,
  config: BandFilterConfig,
  memo: Map<string, number[]>,
  memoKeyPrefix: string
): T => {
  const cutoff = config.cutoffFor(label);
  if (!cutoff || !isFlatNumericArray(values)) return values;

  const fs = config.sampleRateFor(label, values);
  if (!fs) return values;

  const key = `${memoKeyPrefix}${label}|${cutoff.lowCut}|${cutoff.highCut}|${fs}`;
  const cached = memo.get(key);
  if (cached) return cached as T;

  let filtered: number[];
  try {
    filtered = filterSignalBand(values, cutoff.lowCut, cutoff.highCut, fs);
  } catch {
    filtered = values;
  }
  memo.set(key, filtered);
  return filtered as T;
};

export const filterSignalMap = <T extends number[] | number[][]>(
  signal: Record<string, T>,
  config: BandFilterConfig,
  memoOwner: object,
  memoKeyPrefix = ''
): Record<string, T> => {
  const memo = getMemoFor(memoOwner);
  const result: Record<string, T> = {};
  for (const [label, values] of Object.entries(signal)) {
    result[label] = filterOneSignal(label, values, config, memo, memoKeyPrefix);
  }
  return result;
};

const yieldToMainThread = (): Promise<void> => {
  const schedulerYield = (globalThis as SafeAny)?.scheduler?.yield;
  if (typeof schedulerYield === 'function') {
    return schedulerYield.call((globalThis as SafeAny).scheduler);
  }
  return new Promise((resolve) => setTimeout(resolve, 0));
};

export const filterSignalMapChunked = async <T extends number[] | number[][]>(
  signal: Record<string, T>,
  config: BandFilterConfig,
  memoOwner: object,
  memoKeyPrefix = '',
  abortSignal?: AbortSignal
): Promise<Record<string, T>> => {
  const memo = getMemoFor(memoOwner);
  const result: Record<string, T> = {};
  for (const [label, values] of Object.entries(signal)) {
    if (abortSignal?.aborted) {
      throw new DOMException('Filtering aborted', 'AbortError');
    }
    result[label] = filterOneSignal(label, values, config, memo, memoKeyPrefix);
    await yieldToMainThread();
  }
  return result;
};
